import { FileCategory } from "constants/filenames";
import { DateParserArguments, Path, UnionType, WranglerData } from "fileParsing/wrangler/types";
export { FileCategory };

// Provider attributes definition, see strings.json for definitions of each provider
export type ProviderDefinition = {
  // Full name of Provider, Example:
  // name: "Facebook"
  name: string;
  // id used to link ProviderFile's to ProviderDefinitions
  id: string;
  // Author of provider definition, e.g. John Doe <john.doe@example.com>
  author: string;
  // Date of last update
  date: string;
  // Incrementing version number. +1 for each export
  version: number;
  // URL to the Website of the Provider
  // url: www.facebook.com
  url: string;
  // Description of how the DataExport can be accessed
  strings: {
    en: {
      name: string;
      help: string[];
    };
    de: {
      name: string;
      help: string[];
    };
  };
  screenshots?: (string | undefined)[];
  // If Provider is WIP and should not be shown in the Tool
  hidden?: boolean;
  instructions: ExportType[];
  fileStructure: string[];
  optionalFileStructure: string[];
};

export type DataPointExportDefinition = {
  path: Path;
  schema: UnionType;
  category?: Category;
  subcategory: ({ type: "data"; value: Path } | { type: "text"; value: string })[];
  content: ({ type: "data"; value: Path } | { type: "text"; value: string })[];
  selectedTimestamps: { path: Path; arguments: DateParserArguments }[];
  selectedUris: Path[];
  selectedContacts: Path[];
  selectedLatitudes: Path[];
  selectedLongitudes: Path[];
};

export type ExportType = {
  schema: UnionType;
  templates: DataPointExportDefinition[];
  hidden: boolean;
};

export type ProviderParser = {
  fileStructure: string[];
  optionalFileStructure: string[];
  instructions: ExportType[];
};

export type Provider = ProviderDefinition & ProviderParser;

/**
 * Each single file from a export is parsed into an object of this type definition.
 * It has several attributes common to a file.
 * Based on the {@link FileCategory}, it can have different additional data.
 * When it is a textfile, it might have a list of {@link DataPoint}s that were parsed from the raw data inside that file.
 * It could also have a {@link WranglerData} which defines how far along the wrangler has parsed that file.
 * When it is an image, audio or video file, it can have `binaryData`.
 *
 * @property {string} id: only unique among this data export
 * @property {number} exportId: file item id
 * @property {string} fileName: file name
 * @property {@link FileCategory} fileType: file type
 * @property {string} providerId: Id of Provider, currently only single character as
 * @property {number} compressedSize: Compressed Size of File in the Zip in Bytes
 * @property {number} uncompressedSize: Uncompressed Byte Size of that File
 * @property {Date} date: Date when the File was created
 * @property {string} dataCategory: Category of {@link DataPoint}s in the File. If none are contained, it is a FileCategory
 * @property {string} folder: path of folder
 * @property {string} hash: deprecated
 * @property {boolean} isDir: boolean if it is a File or Directory
 * @property {string} path: Full Path of File
 * @property {DataPoint[]} data: List of all {@link DataPoints} that were extracted of that File
 * @property {string} groupBy: deprecated
 * @property {string} mimeType: Mime Type, Example: "image/jpeg"
 * @property {null | string} binaryData: When it contains binary Data, for example an Image, this contains a link to that binary data, which is stored with URL.createObjectURL(),
 * @property {@link WranglerData} parserData: Current wrangler data
 */
export type ProviderFile = {
  id: number;
  exportId: number;
  providerId: string;
  compressedSize: number;
  fileName: string;
  zipName: string;
  fileType: FileCategory;
  dataCategory: Category;
  folder: string;
  hash: string;
  isDir: boolean;
  path: string;
  data: DataPoint[];
  uncompressedSize: number;
  groupBy: string;
  date: Date;
  mimeType: string;
  binaryData: null | string;
  parserData: WranglerData;
};

export type ProviderFileRef = {
  id: number;
  exportId: number;
  providerId: string;
  hash: string;
  path: string;
};

export type UnparsedFile = {
  compressedSize: number;
  fileName: string;
  folder: string;
  hash: string;
  isDir: boolean;
  path: string;
  data: Uint8Array | null;
  uncompressedSize: number;
  date: Date;
  mimeType: string;
  binaryData: null | string;
};

export function toDataPoint(
  date: Date,
  text: string,
  category: Category,
  subcategory: string,
  uri?: string,
  contacts?: string[],
  longlat?: [number, number],
  policyCategory?: string[],
  purposes?: Set<string>,
  sharing?: Set<string>,
  retention?: Set<string>,
): DataPoint {
  return {
    date,
    text,
    category,
    subcategory,
    uri,
    contacts,
    longlat,
    policyCategory,
    purposes,
    sharing,
    retention,
  };
}

/**
 * Data points represent individual points of data, which
 * could be identified in the machine-readable raw files.
 * Most of the raw data is given in an array
 * with individual elements that contain multiple relevant attributes.
 * These attributes are parsed into this standartied format which is
 * primarily time oriented. All non-relevant attributes are collected
 * and expressed as a string in the text attribute.
 *
 * @property {Date} date: The moment the action or event happened.
 * @property {string} text: contains all information of other attributes and content
 * @property {string} subcategory: More specific description of content
 * @property {Category} category: One of 10 {@link Category}s
 * @property {string} [uri]: Contains URI to media file, which is included in the export
 * @property {number} [sensitvity]: deprecated, number that can be set in the ListView
 * @property {string[]} [contacts]: list of names to parse who talkts to whom, mostly in the Message category
 * @property {[number,number]} [longlat]: a location where that event happened
 *
 */
export type DataPoint = {
  readonly date: Date;
  readonly text: string;
  readonly subcategory: string;
  readonly category: Category;
  readonly uri?: string;
  sensitivity?: number;
  readonly contacts?: string[];
  readonly longlat?: [number, number];
  policyCategory?: string[];
  purposes?: Set<string>;
  sharing?: Set<string>;
  retention?: Set<string>;
};

/**
 * The ten primary Category to determine DataPoints.
 * One of the following:
 *
 * * Account = 'Account',
 * * Activity = 'Activity',
 * * Contacts = 'Contacts',
 * * Location = 'Location',
 * * Media = 'Media',
 * * Messages = 'Messages',
 * * Likes = 'Likes',
 * * Other = 'Other',
 * * PostsAndComments = 'Posts',
 * * Security = 'Security'
 */
export enum Category {
  Account = "Account",
  Activity = "Activity",
  Contacts = "Contacts",
  Location = "Location",
  Media = "Media",
  Messages = "Messages",
  Likes = "Likes",
  Other = "Other",
  PostsAndComments = "Posts",
  Security = "Security",
}

export enum WorkerStatus {
  Start = "Start",
  Failure = "Failure",
  Log = "Log",
  Progress = "Progress",
  Success = "Success",
  Warn = "Warn",
  Provider = "Provider",
}

export type DataProviderOptions = {
  readonly files: string[];
  readonly folders: string[];
};

export type JSZipCompressedObject = {
  compressedSize: number;
  uncompressedSize: number;
  crc32: number;
  compression: object;
  compressedContent: string | ArrayBuffer | Uint8Array | Buffer;
};
